import { TrashIcon, DocumentDuplicateIcon } from '@heroicons/react/24/solid';
import {
  Node,
  useNodes,
  useEdges,
  NodeToolbar,
  useStoreApi,
  useReactFlow,
} from '@xyflow/react';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Button } from 'src/components/button';
import { DeleteLineIcon } from 'src/components/icons/DeleteLineIcon';
import { EditLineIcon } from 'src/components/icons/EditLineIcon';
import { GroupIcon } from 'src/components/icons/GroupIcon';
import { RotateRightIcon } from 'src/components/icons/RotateRightIcon';
import { appConfig, defaultConnectionData } from 'src/config';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { useUndoRedo } from 'src/context/undo-redo';
import { EEvents, RotateNodesEvent } from 'src/types/events';
import {
  getNewDuplicateNode,
  syncNodeIpInterfacesAndValidate,
} from 'src/utils/node';
import { twMerge } from 'tailwind-merge';

import { DialogCustomEdgeEdit } from '../edges/dialog-edit';
import { IEdgeExtraData } from '../edges/types';
import { EDataType } from '../models/node-data';
import { NodeModel } from '../models/node-model';
import { generateNodeId } from '../models/node-utils';

const padding = 25;

export default function SelectedNodesToolbar() {
  const nodes = useNodes();
  const edges = useEdges();
  const { vlans } = useGlobalContext();
  const { takeSnapshot } = useUndoRedo();
  const { setNodes, getNodesBounds, setEdges } = useReactFlow();
  const store = useStoreApi();
  const selectedNodes = nodes.filter((node) => node.selected && !node.parentId);
  const selectedNodeIds = selectedNodes.map((node) => node.id);
  const isVisibleNodeToolbar = selectedNodeIds.length > 1;

  const [isEditingEdge, setIsEditingEdge] = useState(false);
  const [edgeData, setEdgeData] = useState<IEdgeExtraData>(
    defaultConnectionData,
  );

  const selectedEdges = edges.filter((edge) => edge.selected);
  const selectedEdgeIds = selectedEdges.map((edge) => edge.id);
  const isVisibleEdgeToolbar = selectedEdges.length > 1;

  useEffect(() => {
    const handleSelectionClick = (event: MouseEvent) => {
      const selectionRect = document.querySelector(
        '.react-flow__nodesselection-rect',
      );
      if (selectionRect && event.shiftKey) {
        const reactFlowBounds = document
          .querySelector('.react-flow')
          ?.getBoundingClientRect();
        if (!reactFlowBounds) return;

        // Calculate the click position relative to react-flow
        const x = event.clientX - reactFlowBounds.left;
        const y = event.clientY - reactFlowBounds.top;

        // Find the nearest node to the click position
        const clickedNode = nodes.find((node) => {
          const nodeElement = document.querySelector(`[data-id="${node.id}"]`);
          if (!nodeElement) return false;

          const nodeBounds = nodeElement.getBoundingClientRect();
          const nodeX = nodeBounds.left - reactFlowBounds.left;
          const nodeY = nodeBounds.top - reactFlowBounds.top;

          return (
            x >= nodeX &&
            x <= nodeX + nodeBounds.width &&
            y >= nodeY &&
            y <= nodeY + nodeBounds.height
          );
        });

        if (clickedNode) {
          setNodes((nds) =>
            nds.map((node) =>
              node.id === clickedNode.id
                ? {
                    ...node,
                    selected: !node.selected,
                  }
                : node,
            ),
          );
        }
      }
    };

    document.addEventListener('click', handleSelectionClick);
    return () => {
      document.removeEventListener('click', handleSelectionClick);
    };
  }, [setNodes, nodes]);

  const onGroup = () => {
    const rectOfNodes = getNodesBounds(selectedNodes);
    const groupId = `${EDataType.Group}-${generateNodeId()}`;
    const parentPosition = {
      x: rectOfNodes.x,
      y: rectOfNodes.y,
    };
    const groupNode = {
      id: groupId,
      type: EDataType.Group,
      position: parentPosition,
      style: {
        width: rectOfNodes.width + padding * 2,
        height: rectOfNodes.height + padding * 2,
      },
      data: {},
    };

    const nextNodes: Node[] = nodes.map((node) => {
      if (selectedNodeIds.includes(node.id)) {
        return {
          ...node,
          position: {
            x: node.position.x - parentPosition.x + padding,
            y: node.position.y - parentPosition.y + padding,
          },
          extent: 'parent',
          parentId: groupId,
        };
      }

      return node;
    });

    store.getState().resetSelectedElements();
    store.setState({ nodesSelectionActive: false });
    takeSnapshot();
    setNodes([groupNode, ...nextNodes]);
  };

  const onDeleteNode = () => {
    takeSnapshot();
    const nodeIds = selectedNodes.map((node) => node.id);
    const nextNodes = nodes.filter((node) => !nodeIds.includes(node.id));
    const allAvailableNodeIds = nextNodes.map((node) => node.id);
    setNodes(nextNodes);
    // clear edges no source or target is in nodeIds
    setEdges(
      edges.filter(
        (edge) =>
          !nodeIds.includes(edge.source) &&
          !nodeIds.includes(edge.target) &&
          allAvailableNodeIds.includes(edge.source) &&
          allAvailableNodeIds.includes(edge.target),
      ),
    );
  };

  const onDeleteEdge = () => {
    takeSnapshot();
    const edgeIds = selectedEdges.map((edge) => edge.id);
    setEdges(edges.filter((edge) => !edgeIds.includes(edge.id)));
  };

  const handleRotate = () => {
    takeSnapshot();
    // create event to rotate nodes
    const eventData: RotateNodesEvent = {
      nodeIds: selectedNodeIds,
    };
    const event = new CustomEvent(EEvents.ROTATE_NODES, {
      detail: eventData,
    });
    window.dispatchEvent(event);
  };

  const onDuplicate = () => {
    const currentNodes = [...nodes];
    const rectOfNodes = getNodesBounds(selectedNodes);

    const newNodeIds: Record<string, string> = {};

    selectedNodeIds.forEach((id) => {
      const currentNodePosition = currentNodes.find(
        (node) => node.id === id,
      )?.position;
      const newNode = getNewDuplicateNode(
        currentNodes.find((node) => node.id === id) as Node<NodeModel>,
        currentNodes,
        currentNodePosition?.x ?? 0,
        (currentNodePosition?.y ?? 0) + rectOfNodes.height + 10,
      );
      newNodeIds[id] = newNode.id;
      currentNodes.push({
        ...newNode,
        data: {
          ...newNode.data,
          category: {
            ...newNode.data.category,
            ipInterfaces: [],
          },
        },
      });
    });
    takeSnapshot();
    setNodes([...currentNodes]);

    // duplicate edges
    const currentEdges = [...edges];

    selectedEdges.forEach((edge) => {
      // deep copy edge
      const newEdge = JSON.parse(JSON.stringify(edge));
      if (newNodeIds[edge.source] && newNodeIds[edge.target]) {
        let { id, sourceHandle, targetHandle } = newEdge;

        Object.keys(newNodeIds).forEach((key) => {
          if (id.includes(key)) {
            id = id.replace(key, newNodeIds[key]);
          }
          if (sourceHandle?.includes(key)) {
            sourceHandle = sourceHandle?.replace(key, newNodeIds[key]);
          }
          if (targetHandle?.includes(key)) {
            targetHandle = targetHandle?.replace(key, newNodeIds[key]);
          }
        });

        currentEdges.push({
          ...newEdge,
          id,
          source: newNodeIds[edge.source],
          target: newNodeIds[edge.target],
          sourceHandle,
          targetHandle,
        });
        syncNodeIpInterfacesAndValidate({
          edgeId: id,
          newEdges: [...currentEdges],
          setNodes,
          vlans,
        });
      }
    });

    setEdges([...currentEdges]);
  };

  const onEditEdge = () => {
    const firstEdge = selectedEdges[0];
    setEdgeData({
      ...defaultConnectionData,
      ...(firstEdge?.data as IEdgeExtraData),
    });
    setIsEditingEdge(true);
  };

  const onChangeEdgeData = (newData: Partial<IEdgeExtraData>) => {
    setEdgeData((prev) => ({ ...prev, ...newData }));
  };

  const onSaveMultiEdge = (
    newData: Partial<IEdgeExtraData>,
    closeDialog = true,
  ) => {
    takeSnapshot();
    setEdges((prevEdges) => {
      const newEdges = prevEdges.map((edge) => {
        if (selectedEdgeIds.includes(edge.id)) {
          return {
            ...edge,
            data: {
              ...(edge.data as object),
              ...newData,
            },
          };
        }
        return edge;
      });
      selectedEdgeIds.forEach((edgeId) => {
        syncNodeIpInterfacesAndValidate({
          edgeId,
          newEdges,
          setNodes,
          vlans,
        });
      });
      return newEdges;
    });
    if (closeDialog) {
      setIsEditingEdge(false);
      setEdgeData(defaultConnectionData);
      toast.success('Connections settings saved successfully');
    }
  };

  const renderEdgeActions = () => (
    <>
      <Button
        data-tooltip-id="app-tooltip"
        data-tooltip-content="Edit multiple connections"
        data-testid="selected-nodes-toolbar-edit-button"
        color="blue"
        onClick={onEditEdge}
      >
        <EditLineIcon className="h-5 w-5" />
      </Button>
      <Button
        data-tooltip-id="app-tooltip"
        data-tooltip-content="Delete selected connections"
        data-testid="selected-nodes-toolbar-delete-button"
        color="red"
        onClick={onDeleteEdge}
      >
        <DeleteLineIcon className="h-5 w-5" />
      </Button>
    </>
  );

  return (
    <>
      <NodeToolbar nodeId={selectedNodeIds} isVisible={isVisibleNodeToolbar}>
        <div className="flex flex-row gap-2">
          {appConfig.enableGroupingNode && (
            <Button
              data-tooltip-id="app-tooltip"
              data-testid="selected-nodes-toolbar-group-button"
              data-tooltip-content="Group selected switches and devices"
              color="blue"
              onClick={onGroup}
            >
              <GroupIcon className="h-5 w-5" />
            </Button>
          )}
          {selectedNodeIds.length > 0 && (
            <>
              <Button
                data-tooltip-id="app-tooltip"
                data-testid="selected-nodes-toolbar-delete-button"
                data-tooltip-content="Delete selected switches and devices"
                color="red"
                onClick={onDeleteNode}
              >
                <TrashIcon className="h-5 w-5" />
              </Button>
              <Button
                data-tooltip-id="app-tooltip"
                data-testid="selected-nodes-toolbar-duplicate-button"
                data-tooltip-content="Duplicate selected switches and devices"
                color="yellow"
                onClick={onDuplicate}
              >
                <DocumentDuplicateIcon className="h-4 w-4" />
              </Button>
              <Button
                data-tooltip-id="app-tooltip"
                data-testid="selected-nodes-toolbar-rotate-button"
                data-tooltip-content="Rotate selected switches and devices"
                color="green"
                onClick={handleRotate}
              >
                <RotateRightIcon className="h-4 w-4" />
              </Button>
            </>
          )}
          {selectedEdges.length > 0 && renderEdgeActions()}
        </div>
      </NodeToolbar>
      {!isVisibleNodeToolbar && isVisibleEdgeToolbar && (
        <div
          id="selected-edges-toolbar"
          className={twMerge([
            'absolute left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 transform',
            'rounded-sm border border-dashed border-blue-500 bg-blue-500 bg-opacity-20 p-1',
            'flex flex-row gap-2',
          ])}
        >
          {renderEdgeActions()}
        </div>
      )}
      {isEditingEdge && (
        <DialogCustomEdgeEdit
          ids={selectedEdges.map((edge) => edge.id)}
          onCancel={() => {
            setIsEditingEdge(false);
            setEdgeData(defaultConnectionData);
          }}
          data={edgeData}
          onChangeEdgeData={onChangeEdgeData}
          isMultiple
          onSave={onSaveMultiEdge}
        />
      )}
    </>
  );
}
